import { Alert, AlertProps, AlertTitle, Grow, Snackbar, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

interface AlertMessage extends AlertProps {
  message: string;
  title?: string;
  autoHideDuration?: number | null; // use null to persist indefinitely
}

type SnackbarContextValue = {
  setAlert: (alert: AlertMessage | undefined) => void;
};

export const SnackbarContext = createContext<SnackbarContextValue>({
  setAlert: () => {},
});

const StableChildren = React.memo(function StableChildren({
  children,
}: {
  children: React.ReactNode;
}) {
  return <>{children}</>;
});

function SnackbarProvider({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [alert, setAlertState] = useState<AlertMessage>();

  const setAlert = useCallback((next: AlertMessage | undefined) => {
    // default value for autoHideDuration will be applied in the Snackbar component if not provided
    if (next != null && next?.autoHideDuration === undefined) {
      next = { ...next, autoHideDuration: next?.severity === 'error' ? 6000 : 3500 };
    }

    setAlertState(next);
  }, []);

  const ctxValue = useMemo(() => ({ setAlert }), [setAlert]);

  const handleClose = useCallback((_: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  }, []);

  useEffect(() => {
    if (alert != null) setOpen(true);
  }, [alert]);

  const alertKey = `${alert?.severity ?? 'info'}:${alert?.message ?? ''}`;
  const autoHideDuration = alert?.autoHideDuration ?? (alert?.severity === 'error' ? 6000 : 3500);
  const alertTitle = alert?.title;
  const hasAlertTitle = alertTitle != null && alertTitle.trim().length > 0;

  return (
    <SnackbarContext.Provider value={ctxValue}>
      <StableChildren>{children}</StableChildren>

      <Snackbar
        key={alertKey}
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        TransitionComponent={Grow}
        sx={{
          pointerEvents: 'none',
          '& .MuiAlert-root': {
            pointerEvents: 'auto',
            minWidth: { xs: 'calc(100vw - 24px)', sm: 360 },
            maxWidth: 'min(680px, calc(100vw - 24px))',
            boxSizing: 'border-box',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            borderRadius: 2,
            borderWidth: 1,
            boxShadow: theme.shadows[8],
            backdropFilter: 'blur(6px)',
            borderColor: alpha(theme.palette.common.black, 0.12),
          },
        }}
      >
        <Alert
          onClose={handleClose}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          severity={alert?.severity}
          sx={{
            py: 1.25,
            px: 1.75,
            alignItems: 'flex-start',
            '& .MuiAlert-icon': {
              mt: '2px',
              fontSize: 22,
            },
            '& .MuiAlert-message': {
              fontWeight: 500,
              lineHeight: 1.35,
              width: '100%',
            },
          }}
          elevation={0}
          variant='outlined'
        >
          {hasAlertTitle ? <AlertTitle sx={{ mb: 0.25 }}>{alertTitle}</AlertTitle> : null}
          <Typography
            component='span'
            variant='body2'
            sx={{
              display: 'block',
              fontWeight: 400,
              whiteSpace: 'pre-line',
              lineHeight: 1.45,
            }}
          >
            {alert?.message}
          </Typography>
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export default SnackbarProvider;
